import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { LocalStorageKeys } from "@repo/logic";
import { useQueryKeyFactory } from "../../context/QueryKeyProvider";
import { getIsTimeToPrompt, useAMLPrompt } from "../../hooks/useAMLPrompt";
import { usePostLogin } from "../../queryHooks";
import { AMLRedirect } from "../AML";
import { AMLFormStandaloneDrawer } from "../AML/Form";
import { BankIdIdentification } from "../AML/identification";
import { AgreementDrawer } from "../Drawer/AgreementDrawer/AgreementDrawer";

export enum Drawers {
    Agreement = "Agreement",
    Id = "Id",
    AML = "AML",
}

type IDrawerManager = {
    disableAgreementDrawer?: boolean;
    disableAmlAndBankIdDrawers?: boolean;
};

export const DrawerManager = ({
    disableAgreementDrawer = false,
    disableAmlAndBankIdDrawers = false,
}: IDrawerManager) => {
    const { data, isSuccess, isFetching } = usePostLogin({
        options: { enabled: !disableAgreementDrawer },
    });
    const amlPrompt = useAMLPrompt();
    const queryClient = useQueryClient();
    const { createQueryKey } = useQueryKeyFactory();

    const [drawerQueue, setDrawerQueue] = useState<Drawers[]>([]);
    const [currentDrawerIndex, setCurrentDrawerIndex] = useState(0);

    useEffect(() => {
        const newPrompts: Drawers[] = [];
        const pendingQueue = drawerQueue.slice(currentDrawerIndex);
        const timeToPrompt = amlPrompt ? getIsTimeToPrompt(amlPrompt) : false;
        if (
            !disableAgreementDrawer &&
            isSuccess &&
            !isFetching &&
            data.startConsentGathering &&
            !pendingQueue.includes(Drawers.Agreement)
        ) {
            newPrompts.push(Drawers.Agreement);
        }
        if (
            !disableAmlAndBankIdDrawers &&
            amlPrompt &&
            amlPrompt.ID &&
            !pendingQueue.includes(Drawers.Id) &&
            timeToPrompt &&
            timeToPrompt.promptID
        ) {
            newPrompts.push(Drawers.Id);
        }
        if (
            !disableAmlAndBankIdDrawers &&
            amlPrompt &&
            amlPrompt.AML &&
            !pendingQueue.includes(Drawers.AML) &&
            timeToPrompt &&
            timeToPrompt.promptAML
        ) {
            newPrompts.push(Drawers.AML);
        }
        if (newPrompts.length > 0) {
            setDrawerQueue((prev) => [...prev, ...newPrompts]);
        }
    }, [
        isSuccess,
        isFetching,
        data,
        drawerQueue,
        currentDrawerIndex,
        amlPrompt,
        disableAgreementDrawer,
        disableAmlAndBankIdDrawers,
    ]);

    const bankIdCloseCallback = () => {
        localStorage.setItem(LocalStorageKeys.LAST_BANK_ID_CHECK, Date.now().toString());
        setCurrentDrawerIndex((prev) => prev + 1);
    };

    const amlCloseCallback = () => {
        localStorage.setItem(LocalStorageKeys.LAST_AML_CHECK, Date.now().toString());
        setCurrentDrawerIndex((prev) => prev + 1);
    };

    const currentDrawer = ((): Drawers | null => {
        if (currentDrawerIndex >= drawerQueue.length) {
            return null;
        }
        return drawerQueue[currentDrawerIndex];
    })();
    return (
        <>
            <AgreementDrawer
                open={currentDrawer === Drawers.Agreement}
                onClose={() => {
                    setCurrentDrawerIndex((prev) => prev + 1);
                    queryClient.invalidateQueries({
                        queryKey: createQueryKey({ key: ["getPostLogin"] }),
                    });
                }}
            />
            {amlPrompt && (
                <BankIdIdentification
                    action={{
                        redirect: AMLRedirect.Default,
                        contractId: amlPrompt.contractId,
                        contractType: amlPrompt.contractType,
                    }}
                    onClick={bankIdCloseCallback}
                    onCloseCallback={bankIdCloseCallback}
                    scenario="default"
                    open={currentDrawer === Drawers.Id}
                />
            )}
            {amlPrompt && (
                <AMLFormStandaloneDrawer
                    amlProps={{
                        contractId: amlPrompt.contractId,
                        contractType: amlPrompt.contractType,
                        onCloseCallback: amlCloseCallback,
                    }}
                    open={currentDrawer === Drawers.AML}
                />
            )}
        </>
    );
};
